import * as Slider from '@studio/design-system/slider';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Control } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type {
  DeviceRegistryCommandFunctionType,
  DeviceRegistryCommandKey
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import { DeviceRegistryInterfaceItemControlType } from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useState, useRef } from 'react';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import type { Proxy } from '#~/agent/index.js';
import type { SettingsStatus } from './status-notifications.js';
import { STATUS_NOTIFICATIONS } from './status-notifications.js';
import type { Device } from '@studio/models/device';

interface DeviceSettingSliderProps {
  agent?: T_Maybe<Proxy>;
  className: string;
  cmdKey: DeviceRegistryCommandKey;
  controlType: DeviceRegistryInterfaceItemControlType;
  defaultValue: T_Maybe<number>;
  deviceId: Device['id'];
  disabled?: boolean;
  displayMin?: number;
  displayMax?: number;
  label: string;
  onMutation: T_Maybe<(mutation: DeepPartial<Control>) => void>;
  onReactiveMutation?: T_Maybe<(mutation: DeepPartial<Control>) => Promise<void>>;
  value: number;
  onControlClick?: (controlData: {
    label: string;
    controlType: DeviceRegistryInterfaceItemControlType;
    key: DeviceRegistryCommandKey;
    value?: DeviceRegistryCommandFunctionType;
  }) => void;
  status?: SettingsStatus;
}

/** `<DeviceSettingSlider>` component. */
export const DeviceSettingSlider = ({
  agent,
  className,
  cmdKey,
  controlType,
  defaultValue,
  displayMin,
  displayMax,
  deviceId,
  disabled,
  label,
  onMutation,
  onReactiveMutation,
  value,
  onControlClick,
  status
}: DeviceSettingSliderProps): ReactElement => {
  if (displayMin === undefined || displayMax === undefined || displayMin >= displayMax) {
    displayMin = 0;
    displayMax = 1;
  }

  const step = displayMax - displayMin >= 2 ? 1 : 0.01;
  const intValue = value * (displayMax - displayMin) + displayMin;
  defaultValue =
    defaultValue !== undefined ? defaultValue * (displayMax - displayMin) + displayMin : undefined;

  const [displayValue, setDisplayValue] = useState<number | undefined>(undefined);
  const [interfaceTimeout, setInterfaceTimeout] = useState<NodeJS.Timeout | undefined>(undefined);

  const isMutating = useRef(false);
  const pendingMutation = useRef<number | null>(null);

  const executeMutation = async (normalizedValue: number) => {
    if (isMutating.current) {
      // If there is a pending mutation, we will overwrite it
      pendingMutation.current = normalizedValue;
      return;
    }

    isMutating.current = true;

    const control: DeepPartial<Control> = {
      device: {
        [deviceId]: {
          deviceSettings: {
            [String(cmdKey)]: {
              direct: normalizedValue,
              key: cmdKey,
              lastModified: Date.now()
            }
          }
        }
      }
    };

    try {
      // Use reactive mutation handler if available, otherwise fall back to legacy behavior
      if (onReactiveMutation) {
        await onReactiveMutation(control);
      } else if (onMutation) {
        onMutation(control);
      } else {
        await agent?.mutate({ control }, {});
      }
    } finally {
      isMutating.current = false;

      // Check if there is a pending mutation
      if (pendingMutation.current !== null) {
        const nextValue = pendingMutation.current;
        pendingMutation.current = null;
        await executeMutation(nextValue);
      }
    }

    setDisplayValue(undefined);

    onControlClick?.({
      label,
      controlType: DeviceRegistryInterfaceItemControlType.SLIDER,
      key: cmdKey,
      value: normalizedValue
    });
  };

  const mutateSliderValue = (nextValue: number[]): void => {
    setDisplayValue(nextValue[0]);

    if (nextValue[0] === intValue) {
      return;
    }

    const normalizedValue = ((nextValue[0] ?? intValue) - displayMin) / (displayMax - displayMin);

    clearTimeout(interfaceTimeout);

    const saveTimeout = setTimeout(() => {
      executeMutation(normalizedValue);
    }, 250);

    setInterfaceTimeout(saveTimeout);
  };

  const safeValue = displayValue ?? intValue;

  return (
    <Slider.Container
      canRevert={!disabled && defaultValue !== undefined && safeValue !== defaultValue}
      className={className}
      label={label}
      status={status}
      notification={status ? STATUS_NOTIFICATIONS[status] : undefined}
      onRevert={(): void => {
        mutateSliderValue([defaultValue!]);
      }}
    >
      <Slider.Root
        disabled={!!disabled}
        max={displayMax}
        min={displayMin}
        onChange={mutateSliderValue}
        step={step}
        value={[safeValue]}
      >
        <Slider.Track
          className={twMerge(
            controlType === DeviceRegistryInterfaceItemControlType.SLIDER_RED &&
              'bg-gradient-to-r from-[#00EFEF] to-[#FF0B0B]',
            controlType === DeviceRegistryInterfaceItemControlType.SLIDER_BLUE &&
              'bg-gradient-to-r from-[#FFC604] to-[#0066FF]'
          )}
        />
        <Slider.Thumb />
      </Slider.Root>

      <Slider.Input value={Math.round(safeValue * 100) / 100} />
    </Slider.Container>
  );
};
